import axios from "axios";
import { getCookie } from "../utils/cookieUtils";
import { getGatewayIdToken } from "./gatewaySession";

const mediaApiBaseUrl = import.meta.env.VITE_MEDIA_API;
const centralApiBaseUrl = import.meta.env.VITE_CENTRAL_API;

const mediaApi = axios.create({
  baseURL: centralApiBaseUrl || mediaApiBaseUrl,
});

const centralMediaApi = axios.create({
  baseURL: centralApiBaseUrl,
});

mediaApi.interceptors.request.use(async (config) => {
  config.headers.Authorization = `Bearer ${await getGatewayIdToken()}`;
  return config;
});

export interface Media {
  id: string;
  user_id: string;
  url: string;
  file_name: string;
  mime_type: string;
  extension: string;
  size: number;
  created_at: string;
}

interface PresignedURLResponse {
  mediaID: string;
  presignedURL: string;
}

interface GuestPresignedURLResponse extends PresignedURLResponse {
  capability: string;
  expiresAt: string;
}

interface MediaSignedURLResponse {
  media: Media;
  signedURL: string;
}

export const generateUploadURL = async (
  fileName: string,
  userID: string
): Promise<PresignedURLResponse> => {
  try {
    const response = await mediaApi.post<PresignedURLResponse>(
      "/api/media/upload",
      {
        fileName,
        userID,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error generating upload URL");
    throw new Error("Failed to generate upload URL");
  }
};

export const generateGuestUploadURL = async (
  giftTableID: string,
  fileName: string,
  contentType: string
): Promise<GuestPresignedURLResponse> => {
  const response = await centralMediaApi.post<GuestPresignedURLResponse>(
    "/api/media/guest/upload",
    { giftTableID, fileName, contentType }
  ).catch(() => { throw new Error("Failed to create guest upload"); });
  return response.data;
};

const guestCapabilityHeaders = (capability: string) => ({
  "X-Media-Capability": capability,
});

export const verifyGuestMediaUpload = async (
  id: string,
  capability: string
): Promise<Media> => {
  const response = await centralMediaApi.put<Media>(
    "/api/media/guest/verify",
    undefined,
    { params: { id }, headers: guestCapabilityHeaders(capability) }
  ).catch(() => { throw new Error("Failed to verify guest upload"); });
  return response.data;
};

export const generateGuestGetURL = async (
  id: string,
  capability: string
): Promise<MediaSignedURLResponse> => {
  const response = await centralMediaApi.get<MediaSignedURLResponse>(
    "/api/media/guest/get",
    { params: { id }, headers: guestCapabilityHeaders(capability) }
  ).catch(() => { throw new Error("Failed to read guest media"); });
  return response.data;
};

export const generateGetURL = async (
  id: string
): Promise<MediaSignedURLResponse> => {
  try {
    if (getCookie("authToken")) {
      try {
        return (await mediaApi.get<MediaSignedURLResponse>("/api/media/private/get", { params: { id } })).data;
      } catch (error) {
        // An expired app session must not prevent anonymous cover photos from
        // loading. The public reader still rejects dedications and private media.
        if (!axios.isAxiosError(error) || error.response?.status !== 401) throw error;
      }
    }
    return (await centralMediaApi.get<MediaSignedURLResponse>("/api/media/public/get", { params: { id } })).data;
  } catch (error) {
    console.error("Error generating get URL");
    throw new Error("Failed to generate get URL");
  }
};

export const uploadMedia = async (
  presignedURL: string,
  file: File
): Promise<void> => {
  try {
    await axios.put(presignedURL, file, {
      headers: {
        "Content-Type": file.type || ({jpg:"image/jpeg",jpeg:"image/jpeg",png:"image/png",webp:"image/webp",gif:"image/gif"} as Record<string,string>)[file.name.split(".").pop()?.toLowerCase() || ""] || "application/octet-stream",
      },
    });
  } catch (error) {
    console.error("Error uploading media");
    throw new Error("Failed to upload media");
  }
};

export const verifyMediaUpload = async (id: string): Promise<Media> => {
  try {
    const response = await mediaApi.put<Media>(`/api/media/verify?id=${id}`);
    return response.data;
  } catch (error) {
    console.error("Error verifying media upload");
    throw new Error("Failed to verify media upload");
  }
};

export const generatePermanentURL = async (
  id: string
): Promise<MediaSignedURLResponse> => {
  try {
    const response = await mediaApi.get<MediaSignedURLResponse>(
      "/api/media/get/permanent",
      {
        params: { id },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error generating permanent URL");
    throw new Error("Failed to generate permanent URL");
  }
};

export const getMediaDirectURL = async (
  id: string
): Promise<{ url: string }> => {
  try {
    const response = await mediaApi.get<{ url: string }>(
      "/api/media/get/direct",
      {
        params: { id },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching media direct URL");
    throw new Error("Failed to fetch media direct URL");
  }
};
